import { UserContextActions } from "./types";

export const USER_CONTEXT_INITIAL_STATE = {
  user: null,
  token: null,
  loading: false,
  applicationData: null,
};

const hasText = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const hasValue = (value: unknown): boolean => {
  if (typeof value === "boolean" || typeof value === "number") {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }

  return hasText(value);
};

const mergeSubscriptionDetails = (
  incomingDetails: unknown,
  persistedDetails: unknown
): Record<string, unknown> | undefined => {
  const normalizedIncoming =
    incomingDetails && typeof incomingDetails === "object"
      ? (incomingDetails as Record<string, unknown>)
      : {};
  const normalizedPersisted =
    persistedDetails && typeof persistedDetails === "object"
      ? (persistedDetails as Record<string, unknown>)
      : {};

  const merged = {
    ...normalizedPersisted,
    ...normalizedIncoming,
  };

  return Object.keys(merged).length > 0 ? merged : undefined;
};

export const mergeUserWithPersistedMembershipState = <
  T extends Record<string, unknown>,
>(
  incomingUser: T | null | undefined,
  persistedUser?: Record<string, unknown> | null
): T | null | undefined => {
  if (!incomingUser || !persistedUser) {
    return incomingUser;
  }

  const incomingId = hasText(incomingUser.id) ? incomingUser.id : null;
  const persistedId = hasText(persistedUser.id) ? persistedUser.id : null;

  if (!incomingId || !persistedId || incomingId !== persistedId) {
    return incomingUser;
  }

  const nextUser: Record<string, unknown> = {
    ...incomingUser,
  };

  const membershipKeys = [
    "subscription",
    "paymentStatus",
    "membershipAutoRenew",
    "membershipBillingCycle",
    "membershipNextRenewalDate",
    "subscriptionDate",
    "paymentDate",
    "transactionId",
  ];

  membershipKeys.forEach((key) => {
    if (!hasValue(nextUser[key]) && hasValue(persistedUser[key])) {
      nextUser[key] = persistedUser[key];
    }
  });

  const mergedSubscriptionDetails = mergeSubscriptionDetails(
    incomingUser.subscriptionDetails,
    persistedUser.subscriptionDetails
  );

  if (mergedSubscriptionDetails) {
    nextUser.subscriptionDetails = mergedSubscriptionDetails;
  }

  return nextUser as T;
};

export const userContextReducer = (
  state: any,
  action: {
    type: UserContextActions;
    payload: any;
  }
) => {
  switch (action.type) {
    case UserContextActions.SET_USER:
      return { ...state, user: action.payload };
    case UserContextActions.SET_TOKEN:
      return { ...state, token: action.payload };
    default:
      return state;
  }
};
